import * as THREE from 'three';
import { DynamicScreen } from '../screen.js';

/* Générateur procédural « smartphone » (utilisé tant qu'aucun .glb n'est fourni).
   params : width/height/depth (mm), fold, hilight, camera ('visor'|'block'),
            finish { frameRoughness, backRoughness, backClearcoat, backClearcoatRoughness } */

const S = .01; // 1 mm = 0.01 unité

function rrShape(w, h, r) {
  r = Math.min(r, w / 2, h / 2); const s = new THREE.Shape(), x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0);
  s.lineTo(x + w, y + h - r); s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2);
  s.lineTo(x + r, y + h); s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
  s.lineTo(x, y + r); s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5); return s;
}
function slabGeo(w, h, d, r, bev) {
  const g = new THREE.ExtrudeGeometry(rrShape(w - 2 * bev, h - 2 * bev, Math.max(.002, r - bev)),
    { depth: Math.max(.0002, d - 2 * bev), bevelEnabled: true, bevelThickness: bev, bevelSize: bev, bevelSegments: 10, curveSegments: 40 });
  g.center(); return g;
}
function panelGeo(w, h, r) {
  const g = new THREE.ShapeGeometry(rrShape(w, h, r), 40), p = g.attributes.position, uv = g.attributes.uv;
  for (let i = 0; i < p.count; i++) uv.setXY(i, (p.getX(i) + w / 2) / w, (p.getY(i) + h / 2) / h);
  return g;
}
const mesh = (geo, mat, x = 0, y = 0, z = 0) => { const o = new THREE.Mesh(geo, mat); o.position.set(x, y, z); return o; };
function anchor(map, parent, id, pos, normal) {
  const o = new THREE.Object3D(); o.name = 'hs_' + id; o.position.set(...pos);
  o.userData.n = new THREE.Vector3(...normal).normalize(); parent.add(o); map.set(id, o); return o;
}
let _logo;
function logoTex() {
  if (_logo) return _logo;
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d');
  g.fillStyle = '#fff'; g.font = '500 210px "Google Sans Flex","Google Sans","Product Sans",Arial,sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('G', 128, 138);
  _logo = new THREE.CanvasTexture(c); _logo.colorSpace = THREE.SRGBColorSpace; return _logo;
}

function makeMaterials(fin = {}) {
  const frame = new THREE.MeshPhysicalMaterial({ name: 'frame', color: '#cccccc', metalness: 1, roughness: fin.frameRoughness ?? .15, clearcoat: .4, clearcoatRoughness: .1, envMapIntensity: 1.25 });
  const back = new THREE.MeshPhysicalMaterial({ name: 'back', color: '#888888', roughness: fin.backRoughness ?? .45, clearcoat: fin.backClearcoat ?? .4, clearcoatRoughness: fin.backClearcoatRoughness ?? .5, sheen: .25, sheenRoughness: .6, sheenColor: new THREE.Color('#ffffff'), envMapIntensity: 1.35 });
  return {
    frame, back,
    glass: new THREE.MeshPhysicalMaterial({ name: 'glass', color: 0x030304, roughness: .03, clearcoat: 1, clearcoatRoughness: .02, envMapIntensity: .6 }),
    visor: new THREE.MeshPhysicalMaterial({ name: 'visor', color: 0x040405, roughness: .05, clearcoat: 1, clearcoatRoughness: .03, envMapIntensity: .45 }),
    lensRing: new THREE.MeshPhysicalMaterial({ name: 'lensRing', color: 0x2b2d31, metalness: 1, roughness: .22, envMapIntensity: 1.2 }),
    lensGlass: new THREE.MeshPhysicalMaterial({ name: 'lensGlass', color: 0x010102, roughness: .04, clearcoat: 1, iridescence: .35, iridescenceIOR: 1.6, iridescenceThicknessRange: [200, 450], envMapIntensity: .4 }),
    lensCore: new THREE.MeshPhysicalMaterial({ name: 'lensCore', color: 0x050b1c, roughness: .1, iridescence: .3, iridescenceIOR: 1.9, iridescenceThicknessRange: [300, 700], envMapIntensity: .4 }),
    flash: new THREE.MeshPhysicalMaterial({ name: 'flash', color: 0xf3eedf, roughness: .35, clearcoat: 1, emissive: 0x2a2416 }),
    hilight: new THREE.MeshBasicMaterial({ name: 'hilight', color: 0x1b1b1e, toneMapped: false }),
    logo: new THREE.MeshPhysicalMaterial({ name: 'logo', map: logoTex(), transparent: true, alphaTest: .05, color: '#dddddd', metalness: .7, roughness: .18, clearcoat: 1 }),
    port: new THREE.MeshStandardMaterial({ name: 'port', color: 0x0b0b0c, roughness: .6 }),
    charge: new THREE.MeshBasicMaterial({ name: 'charge', color: 0xffffff, transparent: true, opacity: 0, toneMapped: false, depthWrite: false })
  };
}

function screenMat(tex) {
  return new THREE.MeshPhysicalMaterial({ color: 0, emissive: 0xffffff, emissiveMap: tex, emissiveIntensity: .95, roughness: .03, clearcoat: 1, clearcoatRoughness: .02, envMapIntensity: .45 });
}

function body(W, H, D, M, { screenTex = null, punch = true } = {}) {
  const g = new THREE.Group(), R = .105, bev = D * .34;
  g.add(mesh(slabGeo(W, H, D, R, bev), M.frame));
  const pw = W - 2 * bev - .004, ph = H - 2 * bev - .004, pr = R - bev;
  const b = mesh(panelGeo(pw, ph, pr), M.back, 0, 0, -D / 2 - .0004); b.rotation.y = Math.PI; g.add(b);
  g.add(mesh(panelGeo(pw + .002, ph + .002, pr), M.glass, 0, 0, D / 2 + .0004));
  if (screenTex) {
    g.add(mesh(panelGeo(pw - .03, ph - .03, pr - .012), screenMat(screenTex), 0, 0, D / 2 + .0009));
    if (punch) g.add(mesh(new THREE.CircleGeometry(.017, 32), new THREE.MeshBasicMaterial({ color: 0 }), 0, ph / 2 - .07, D / 2 + .0012));
  }
  return g;
}
function lens(g, M, x, y, z, r = .06) {
  const ring = mesh(new THREE.CylinderGeometry(r, r, .014, 64), M.lensRing, x, y, z - .005); ring.rotation.x = Math.PI / 2; g.add(ring);
  const add = (geo, mat, dz) => { const m = mesh(geo, mat, x, y, z - dz); m.rotation.y = Math.PI; g.add(m); };
  add(new THREE.CircleGeometry(r * .84, 64), M.lensGlass, .0122);
  add(new THREE.RingGeometry(r * .44, r * .5, 64), M.lensRing, .0124);
  add(new THREE.CircleGeometry(r * .42, 64), M.lensCore, .0123);
}
function flashAndHilight(g, M, x, y, z, hilight) {
  const fl = mesh(new THREE.CircleGeometry(.024, 32), M.flash, x, y, z - .001); fl.rotation.y = Math.PI; g.add(fl);
  if (hilight) { const hr = mesh(new THREE.RingGeometry(.031, .045, 64), M.hilight, x, y, z - .0012); hr.rotation.y = Math.PI; g.add(hr); }
}
function chargeRing(g, M, y, z) {
  const r = mesh(new THREE.RingGeometry(.2, .23, 96), M.charge, 0, y, z); r.rotation.y = Math.PI; g.add(r); return r;
}
function sideButtons(g, M, x, y0) {
  const b1 = mesh(slabGeo(.022, .09, .03, .01, .005), M.frame, x, y0 + .18, 0); b1.rotation.y = Math.PI / 2; g.add(b1);
  const b2 = mesh(slabGeo(.022, .2, .03, .01, .005), M.frame, x, y0, 0); b2.rotation.y = Math.PI / 2; g.add(b2);
}

export function buildPhone(params = {}) {
  const W = (params.width ?? 72) * S, H = (params.height ?? 153) * S;
  const M = makeMaterials(params.finish);
  const anchors = new Map();
  const root = new THREE.Group();
  const out = { root, anchors, roles: { frame: M.frame, back: M.back, logo: M.logo }, materials: M,
    effects: {}, canFold: false, setFold() {}, size: { W, H } };
  out.effects.hilight = params.hilight ? M.hilight : null;
  out.effects.charge = M.charge;

  if (!params.fold) {
    const D = (params.depth ?? 8.5) * S;
    const screen = new DynamicScreen(); out.screen = screen;
    root.add(body(W, H, D, M, { screenTex: screen.texture() }));
    // barre photo
    const vw = W * .87, vh = .19, vy = H / 2 - H * .15, z0 = -D / 2;
    root.add(mesh(slabGeo(vw, vh, .03, vh / 2, .012), M.frame, 0, vy, z0 - .01));
    const gz = z0 - .016; root.add(mesh(slabGeo(vw - .018, vh - .018, .03, (vh - .018) / 2, .013), M.visor, 0, vy, gz));
    const zf = gz - .015, x0 = -vw / 2 + .115, gap = .135;
    for (let i = 0; i < 3; i++) lens(root, M, x0 + i * gap, vy, zf);
    const fx = vw / 2 - .095; flashAndHilight(root, M, fx, vy, zf, params.hilight);
    anchor(anchors, root, 'camera', [x0 + gap, vy, zf - .01], [0, 0, -1]);
    if (params.hilight) anchor(anchors, root, 'hilight', [fx, vy, zf - .01], [0, 0, -1]);
    // détails
    const l = mesh(new THREE.PlaneGeometry(.12, .12), M.logo, 0, -H * .02, -D / 2 - .001); l.rotation.y = Math.PI; root.add(l);
    sideButtons(root, M, W / 2 + .003, H * .02);
    const usb = mesh(slabGeo(.09, .028, .02, .014, .004), M.port, 0, -H / 2 + .006, 0); usb.rotation.x = Math.PI / 2; root.add(usb);
    out.chargeRing = chargeRing(root, M, -H * .02, -D / 2 - .0015);
    anchor(anchors, root, 'pixelsnap', [0, -H * .25, -D / 2 - .002], [0, 0, -1]);
    anchor(anchors, root, 'frame', [W / 2 + .004, -H * .2, 0], [1, 0, 0]);
    anchor(anchors, root, 'usb', [0, -H / 2 - .002, 0], [0, -1, 0]);
    anchor(anchors, root, 'screen', [0, -H * .2, D / 2 + .002], [0, 0, 1]);
    return out;
  }

  // ---------- pliable ----------
  const D = (params.depth ?? 5.6) * S;
  const R = new THREE.Group(); root.add(R); // contenu décalé pendant le pliage
  const inner = new DynamicScreen({ wide: true }), outer = new DynamicScreen();
  out.screen = inner; out.screens = [inner, outer];
  const tA = inner.texture([.5, 1], [0, 0]), tB = inner.texture([.5, 1], [.5, 0]);
  const A = body(W, H, D, M, { screenTex: tA, punch: false }); A.position.x = -W / 2; R.add(A);
  const mw = .3, mh = .42, my = H / 2 - .07 - mh / 2, cx = -W / 2 + .055 + mw / 2;
  A.add(mesh(slabGeo(mw, mh, .03, .09, .012), M.frame, cx, my, -D / 2 - .01));
  A.add(mesh(slabGeo(mw - .018, mh - .018, .03, .082, .012), M.visor, cx, my, -D / 2 - .016));
  const zf = -D / 2 - .031;
  lens(A, M, cx - .062, my + .1, zf, .055); lens(A, M, cx + .062, my + .1, zf, .055); lens(A, M, cx - .062, my - .05, zf, .055);
  flashAndHilight(A, M, cx + .062, my - .05, zf, params.hilight);
  anchor(anchors, A, 'camera', [cx - .062, my + .1, zf - .01], [0, 0, -1]);
  if (params.hilight) anchor(anchors, A, 'hilight', [cx + .062, my - .05, zf - .01], [0, 0, -1]);
  const l = mesh(new THREE.PlaneGeometry(.12, .12), M.logo, 0, -H * .05, -D / 2 - .001); l.rotation.y = Math.PI; A.add(l);
  out.chargeRing = chargeRing(A, M, -H * .1, -D / 2 - .0015);
  anchor(anchors, A, 'inner', [0, H * .1, D / 2 + .002], [0, 0, 1]);
  anchor(anchors, A, 'pixelsnap', [0, -H * .25, -D / 2 - .002], [0, 0, -1]);
  const pivot = new THREE.Group(); pivot.position.set(0, 0, D / 2); R.add(pivot);
  const B = body(W, H, D, M, { screenTex: tB }); B.position.set(W / 2, 0, -D / 2); pivot.add(B);
  const bev = D * .34, pw = W - 2 * bev - .004, ph = H - 2 * bev - .004;
  const og = mesh(panelGeo(pw + .002, ph + .002, .07), M.glass, 0, 0, -D / 2 - .0006); og.rotation.y = Math.PI; B.add(og);
  const os = mesh(panelGeo(pw - .03, ph - .03, .06), screenMat(outer.texture()), 0, 0, -D / 2 - .001); os.rotation.y = Math.PI; B.add(os);
  anchor(anchors, B, 'outer', [0, H * .1, -D / 2 - .002], [0, 0, -1]);
  sideButtons(B, M, W / 2 + .003, H * -.05);
  anchor(anchors, B, 'frame', [W / 2 + .004, -H * .2, 0], [1, 0, 0]);
  R.add(mesh(new THREE.CylinderGeometry(D / 2, D / 2, H - .05, 40), M.frame));
  anchor(anchors, R, 'hinge', [0, -H * .3, -D / 2 - .002], [0, 0, -1]);
  out.canFold = true;
  out.size = { W: W * 2, H };
  out.setFold = t => { pivot.rotation.y = -(1 - t) * Math.PI; R.position.x = (1 - t) * W / 2; out.size.W = W * (1 + t); };
  out.setFold(1);
  return out;
}
